import { TFunction } from 'i18next'
import { CUSTOMER_TYPES, PRODUCTS_CONFIG, REGIONS } from '@boltenergy-be/shared'
import { PRODUCT_PROMOS } from 'components/promo-code/constants'
import { Language } from 'types/language'
import { getUpperCaseLanguage } from 'utils/language'

/**
 * Builds the condition bullets for a plenty promo code, or null when the code has no connected products
 * @param {string} code
 * @param {Language} language
 * @param {TFunction} t
 * @returns {string[] | null}
 */
export const getPlentyPromoConditions = (code: string, language: Language, t: TFunction): string[] | null => {
  const connectedPromoConfigs = PRODUCT_PROMOS.filter(
    (promo) => promo.code[CUSTOMER_TYPES.residential] === code || promo.code[CUSTOMER_TYPES.professional] === code
  )
  const uppercaseLanguage = getUpperCaseLanguage(language)
  const promoProducts = [...new Set(connectedPromoConfigs.map(({ product }) => product))]
  const productNames = promoProducts.map((product) => PRODUCTS_CONFIG[product]?.name?.[uppercaseLanguage]).filter(Boolean)

  if (!productNames.length) {
    return null
  }

  const regionAvailability = [...new Set(connectedPromoConfigs.flatMap(({ availableInRegion }) => availableInRegion))]
  const translatedRegions = regionAvailability.map((region) => t(`common:regions.${region.toLowerCase()}`))
  const showRegions = regionAvailability.length > 0 && regionAvailability.length < Object.keys(REGIONS).length
  const eligibleCustomerTypes = Object.values(CUSTOMER_TYPES).filter((type) =>
    connectedPromoConfigs.some((promo) => promo.code[type] === code)
  )
  const listFormatter = new Intl.ListFormat(language, { type: 'conjunction' })

  return [
    t('terms.promo.plentyPromo.conditions.products', {
      count: productNames.length,
      products: listFormatter.format(productNames)
    }),
    showRegions &&
      t('terms.promo.plentyPromo.conditions.regions', {
        count: regionAvailability.length,
        regions: listFormatter.format(translatedRegions)
      }),
    eligibleCustomerTypes.length === 1 &&
      t('terms.promo.plentyPromo.conditions.customerType', { context: eligibleCustomerTypes[0].toLowerCase() })
  ].filter((condition): condition is string => !!condition)
}
